import {
  Button,
  Label,
  ListBox,
  ListBoxItem,
  ListBoxItemProps,
  Popover,
  Select,
  SelectValue,
} from 'react-aria-components';
import classNames from 'classnames';

import { DownArrowIcon } from '../icons';

import * as styles from './SelectInput.css';

export type SelectInputOption<TOptionKey extends string> = {
  /**
   * The name/content to display for the option in the dropdown
   */
  name: string | React.ReactNode;
  /**
   * A text-only readable name for the option, used for accessibility purposes.
   */
  textName?: string;
  /**
   * The unique key to represent each option
   */
  key: TOptionKey;
  /**
   * Additional props to pass to the ListBoxItem component for this option
   */
  itemProps?: Omit<ListBoxItemProps, 'children'> & {
    // Allow any data-* attributes to be passed through
    [key: `data-${string}`]: string;
  };
} & (
  | {
      name: string;
      textName?: string;
    }
  | {
      // If the name is a React node, we must supply an additional string-only textName for screen readers
      name: React.ReactNode;
      textName: string;
    }
);

interface SelectInputProps<TOptionKey extends string> {
  id?: string;
  label?: React.ReactNode;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  className?: string;
  buttonClassName?: string;
  labelClassName?: string;
  listBoxClassName?: string;
  /**
   * Class name applies to the span wrapping the currently displayed selected value
   * label for the select input
   */
  selectedValueWrapperClassName?: string;
  /**
   * Additional props to apply to each ListBoxItem component for each option
   */
  optionItemProps?: Omit<ListBoxItemProps, 'children'>;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  placeholder?: string;
  isDisabled?: boolean;
  options: ReadonlyArray<SelectInputOption<TOptionKey>>;
  /**
   * The currently selected key for the select input. This can be null
   * if no option is selected.
   */
  selectedKey: TOptionKey | null;
  onSelectionChange: (newSelectedKey: TOptionKey) => void;
}

/**
 * A controlled select input component that allows users to select from a list of options.
 */
export const SelectInput = <TOptionKey extends string>({
  id,
  label,
  ariaLabel,
  ariaLabelledBy,
  options,
  selectedKey,
  onSelectionChange,
  isOpen,
  onOpenChange,
  placeholder,
  isDisabled = false,
  className,
  buttonClassName,
  listBoxClassName,
  selectedValueWrapperClassName,
  optionItemProps,
  labelClassName,
}: SelectInputProps<TOptionKey>) => {
  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      selectedKey={selectedKey}
      onSelectionChange={(newSelectedKey) => onSelectionChange(newSelectedKey as TOptionKey)}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className={classNames(styles.selectInput, className)}
      isDisabled={isDisabled}
      placeholder={placeholder}
    >
      {label ? <Label className={classNames(styles.label, labelClassName)}>{label}</Label> : null}
      <Button className={classNames(styles.selectButton, buttonClassName)}>
        <SelectValue className={classNames(styles.selectedValue, selectedValueWrapperClassName)} />
        <DownArrowIcon aria-hidden="true" className={styles.arrowIcon} />
      </Button>
      <Popover className={styles.popover}>
        <ListBox items={options} className={classNames(styles.listBox, listBoxClassName)}>
          {(option) => {
            const listBoxItemProps = {
              // Apply base option props, then option-specific props
              ...optionItemProps,
              ...option.itemProps,
            };
            // Ensure all classNames are merged
            listBoxItemProps.className = classNames(
              styles.listBoxItem,
              optionItemProps?.className,
              option.itemProps?.className,
            );

            return (
              <ListBoxItem {...listBoxItemProps} textValue={option.textName}>
                {option.name}
              </ListBoxItem>
            );
          }}
        </ListBox>
      </Popover>
    </Select>
  );
};
